"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { AlertCircleIcon, ExternalLinkIcon, Loader2Icon } from "lucide-react";
import Link from "next/link";
import { type ChangeEvent, type ReactNode, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAiChatUiContext } from "@/lib/ai/ai-chat-ui-context";
import type { AgentUIMessage } from "@/lib/ai/types";
import { registerSearchConsoleServiceAccountAction } from "./action";
import { REGISTER_SERVICE_ACCOUNT_NAME } from "./constants";
import type { RegisterSearchConsoleServiceAccountInput } from "./schema";

const GUIDE_URLS = {
  ja: "https://docs.amethy.st/ja/quickstart/index-worker-service-account/",
  en: "https://docs.amethy.st/quickstart/index-worker-service-account/",
} as const;

const CheckPermissionQuery = graphql(`
  query CheckPermissionRegisterSearchConsoleServiceAccount($chatId: ID!) {
    viewer {
      id
      __typename
    }
    node(id: $chatId) {
      __typename
      ... on AgentChat {
        id
        createdByUserID
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

type RegisterResult = {
  ok: boolean;
  serviceAccountId?: string;
  serviceAccountEmail?: string | null;
  message?: string;
};

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function RegisterSearchConsoleServiceAccountConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props): ReactNode {
  const { t, i18n } = useLingui();
  const guideUrl = i18n.locale === "ja" ? GUIDE_URLS.ja : GUIDE_URLS.en;
  const { chatId, workspaceId } = useAiChatUiContext();

  const isTarget =
    isToolUIPart(part) && getToolName(part) === REGISTER_SERVICE_ACCOUNT_NAME;
  const isInputAvailable = part.state === "input-available";
  const input = (part.input ??
    {}) as Partial<RegisterSearchConsoleServiceAccountInput>;

  const [{ data }] = useQuery({
    query: CheckPermissionQuery,
    variables: {
      chatId: typeof chatId === "string" ? safeDecodeURIComponent(chatId) : "",
    },
    pause: !chatId,
  });
  const chatCreatedByUserId =
    data?.node?.__typename === "AgentChat" ? data.node.createdByUserID : null;
  const canRegister = Boolean(
    data?.viewer?.id && data.viewer.id === chatCreatedByUserId,
  );

  const [file, setFile] = useState<File | null>(null);
  const [fetching, setFetching] = useState(false);
  const [result, setResult] = useState<RegisterResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isReady = Boolean(file && file.size > 0);

  if (!isTarget || !isInputAvailable) return null;

  // Lock the mutation target to the active workspace context to avoid cross-workspace writes.
  const organizationId = workspaceId || input.organizationId || "";

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    setFile(selected && selected.size > 0 ? selected : null);
    setError(null);
    setResult(null);
  };

  const handleRegister = async () => {
    setError(null);
    setResult(null);

    if (!canRegister) return;
    if (fetching) return;
    if (!chatId) {
      setError(t`Chat ID is missing. Please reload and try again.`);
      return;
    }
    if (!organizationId.trim()) {
      setError(t`organizationId is missing.`);
      return;
    }
    if (!file || file.size === 0) {
      setError(t`Please upload a JSON file.`);
      return;
    }

    setFetching(true);
    try {
      const form = new FormData();
      form.set("chatId", chatId);
      form.set("organizationId", organizationId);
      form.set("json", file);

      const res = await registerSearchConsoleServiceAccountAction(form);
      if (!res.ok) {
        setError(res.error);
        return;
      }

      const sa = res.serviceAccount;
      if (!sa?.id) {
        setError(
          t`Registration succeeded, but no service account ID was returned.`,
        );
        return;
      }

      const toolOutput = {
        ok: true,
        serviceAccountId: sa.id,
        serviceAccountEmail: sa.email ?? null,
      } as const;

      setResult(toolOutput);
      await addToolResult({
        toolCallId: part.toolCallId,
        tool: getToolName(part),
        output: toolOutput,
      });
      await sendMessage();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setFetching(false);
    }
  };

  const handleCancel = async () => {
    if (!canRegister || fetching) return;

    setFetching(true);
    try {
      await addToolResult({
        toolCallId: part.toolCallId,
        tool: getToolName(part),
        output: { ok: false, cancelled: true },
      });
      await sendMessage();
    } finally {
      setFetching(false);
    }
  };

  return (
    <div className="p-4 text-sm space-y-3">
      <div className="font-medium">
        <Trans>Register a service account</Trans>
      </div>
      <div className="text-muted-foreground">
        <Trans>
          Upload a Service Account JSON key file to register it for this
          workspace.
        </Trans>
      </div>

      <Alert>
        <AlertTitle>
          <Trans>How to proceed</Trans>
        </AlertTitle>
        <AlertDescription>
          <div className="mb-2">
            <Trans>
              The service account registration UI is open. Please follow these
              steps:
            </Trans>
          </div>
          <ol className="list-decimal pl-5 space-y-1">
            <li>
              <Trans>
                Prepare the JSON key file: Download the Service Account JSON key
                file from Google Cloud Console.
              </Trans>
            </li>
            <li>
              <Trans>
                Upload the file: Use the file picker below to upload the JSON
                key file.
              </Trans>
            </li>
            <li>
              <Trans>
                For security, do not paste the key into chat. Upload it via this
                UI.
              </Trans>
            </li>
            <li>
              <Trans>
                After upload, the service account will be registered and it can
                be used to access Search Console data.
              </Trans>
            </li>
          </ol>
          <Button asChild variant="link" className="px-0">
            <Link href={guideUrl} target="_blank" rel="noreferrer">
              <Trans>Open the Index Worker guide</Trans>
              <ExternalLinkIcon className="h-4 w-4" />
            </Link>
          </Button>
        </AlertDescription>
      </Alert>

      <div className="space-y-2">
        <div className="text-muted-foreground">
          <Trans>Upload the downloaded JSON key file.</Trans>
        </div>
        <Input
          type="file"
          accept="application/json,.json"
          onChange={handleFileChange}
          disabled={fetching}
          className="cursor-pointer"
        />
      </div>

      {result?.ok ? (
        <Alert>
          <AlertTitle>
            <Trans>Service account registered</Trans>
          </AlertTitle>
          <AlertDescription>
            <div className="space-y-1">
              <div>
                <Trans>ID</Trans>:{" "}
                <span className="font-mono">{result.serviceAccountId}</span>
              </div>
              {result.serviceAccountEmail ? (
                <div>
                  <Trans>Email</Trans>:{" "}
                  <span className="font-mono">
                    {result.serviceAccountEmail}
                  </span>
                </div>
              ) : null}
              <div className="text-muted-foreground">
                <Trans>
                  It should now appear under Settings &gt; Service accounts.
                </Trans>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="destructive">
          <AlertCircleIcon className="h-4 w-4" />
          <AlertTitle>
            <Trans>Error</Trans>
          </AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={handleRegister}
          disabled={!canRegister || fetching || !isReady}
        >
          {fetching ? <Loader2Icon className="h-4 w-4 animate-spin" /> : null}
          <span className={fetching ? "ml-2" : ""}>
            <Trans>Register</Trans>
          </span>
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!canRegister || fetching}
          onClick={() => void handleCancel().catch(() => undefined)}
        >
          <Trans>Cancel</Trans>
        </Button>
      </div>

      {!canRegister ? (
        <div className="text-xs text-muted-foreground">
          <Trans>
            Only the chat creator can register a service account from this
            service account form.
          </Trans>
        </div>
      ) : null}
    </div>
  );
}
